
import { useState, useEffect } from 'react';
import { networkService } from '@/services/network.service';
import { syncService } from '@/services/sync.service';
import OfflineLogger from '@/utils/offlineLogger';

export function useOfflineStatus() {
  const [isOnline, setIsOnline] = useState(networkService.isOnline());
  const [pendingOperations, setPendingOperations] = useState(0);
  const [lastSyncAttempt, setLastSyncAttempt] = useState<Date | null>(null);
  const [syncErrors, setSyncErrors] = useState<string[]>([]);

  // Monitor network status
  useEffect(() => {
    const unsubscribe = networkService.addListener((status) => {
      setIsOnline(status.connected);
      
      if (status.connected) {
        // Auto-sync when coming back online
        setTimeout(() => {
          triggerSync();
        }, 2000);
      }
    });
    
    return unsubscribe;
  }, []);

  // Monitor pending operations count
  useEffect(() => {
    const updatePendingCount = async () => {
      try {
        const count = await syncService.getPendingOperationsCount();
        setPendingOperations(count);
      } catch (error) {
        OfflineLogger.error('useOfflineStatus.updatePendingCount', error);
      }
    };

    // Update initially
    updatePendingCount();

    // Update every 30 seconds
    const interval = setInterval(updatePendingCount, 30000);

    // Listen for sync completion events
    const unsubscribe = syncService.onSyncComplete(() => {
      updatePendingCount();
    });

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, []);

  const triggerSync = async () => {
    if (!isOnline) {
      OfflineLogger.warn('useOfflineStatus.triggerSync', 'Cannot sync while offline');
      return;
    }

    try {
      setLastSyncAttempt(new Date());
      setSyncErrors([]);
      
      await syncService.manualSync();
      
      OfflineLogger.info('useOfflineStatus.triggerSync', 'Manual sync completed successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Sync failed';
      setSyncErrors(prev => [...prev, errorMessage]);
      OfflineLogger.error('useOfflineStatus.triggerSync', error);
    }
  };

  const clearSyncErrors = () => {
    setSyncErrors([]);
  };

  return {
    isOnline,
    pendingOperations,
    lastSyncAttempt,
    syncErrors,
    triggerSync,
    clearSyncErrors,
    hasErrors: syncErrors.length > 0
  };
}
